import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Outlet } from "react-router-dom";
import { ReactLenis } from "lenis/react";
import { ThemeProvider } from "next-themes";
import "@/App.css";

import { AuthProvider } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ProtectedRoute from "@/components/ProtectedRoute";
import AuthCallback from "@/components/AuthCallback";
import { Toaster } from "@/components/ui/sonner";

import Home from "@/pages/Home";
import Features from "@/pages/Features";
import Membership from "@/pages/Membership";
import Resources from "@/pages/Resources";
import ArticlePage from "@/pages/ArticlePage";
import Contact from "@/pages/Contact";
import Join from "@/pages/Join";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Workspace from "@/pages/Workspace";
import MemberHome from "@/pages/MemberHome";
import Onboarding from "@/pages/Onboarding";
import MyProfile from "@/pages/MyProfile";
import ProfilePreview from "@/pages/ProfilePreview";
import Settings from "@/pages/Settings";
import Admin from "@/pages/Admin";
import { BusinessesPage, BusinessDetailPage, ProfessionalsPage, ProfessionalDetailPage } from "@/pages/Directory";
import { EventsPage, EventDetailPage, CreateEventPage } from "@/pages/Events";
import Interests from "@/pages/Interests";
import MyEvents from "@/pages/MyEvents";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function MarketingLayout() {
  return (
    <>
      <Navbar />
      <main className="relative z-10">
        <Outlet />
      </main>
      <Footer />
    </>
  );
}

function AppShell() {
  const location = useLocation();
  // Handle Emergent Google OAuth callback BEFORE any routing/auth check
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route element={<MarketingLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/features" element={<Features />} />
          <Route path="/membership" element={<Membership />} />
          <Route path="/resources" element={<Resources />} />
          <Route path="/resources/:slug" element={<ArticlePage />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/join" element={<Join />} />
        </Route>
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/app" element={<ProtectedRoute><MemberHome /></ProtectedRoute>} />
        <Route path="/app/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
        <Route path="/app/profile" element={<ProtectedRoute><MyProfile /></ProtectedRoute>} />
        <Route path="/app/profile/preview" element={<ProtectedRoute><ProfilePreview /></ProtectedRoute>} />
        <Route path="/app/businesses" element={<ProtectedRoute><BusinessesPage /></ProtectedRoute>} />
        <Route path="/app/businesses/:id" element={<ProtectedRoute><BusinessDetailPage /></ProtectedRoute>} />
        <Route path="/app/professionals" element={<ProtectedRoute><ProfessionalsPage /></ProtectedRoute>} />
        <Route path="/app/professionals/:user_id" element={<ProtectedRoute><ProfessionalDetailPage /></ProtectedRoute>} />
        <Route path="/app/events" element={<ProtectedRoute><EventsPage /></ProtectedRoute>} />
        <Route path="/app/events/create" element={<ProtectedRoute><CreateEventPage /></ProtectedRoute>} />
        <Route path="/app/events/:id" element={<ProtectedRoute><EventDetailPage /></ProtectedRoute>} />
        <Route path="/app/my-events" element={<ProtectedRoute><MyEvents /></ProtectedRoute>} />
        <Route path="/app/interests" element={<ProtectedRoute><Interests /></ProtectedRoute>} />
        <Route path="/app/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        <Route path="/admin" element={<Admin />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
      <AuthProvider>
        <div className="App relative min-h-screen bg-[color:var(--paper)]">
          <div className="noise-overlay" aria-hidden="true" />
          <ReactLenis root options={{ lerp: 0.1, smoothWheel: true }}>
            <BrowserRouter>
              <AppShell />
            </BrowserRouter>
          </ReactLenis>
          <Toaster position="top-center" richColors />
        </div>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
